// @flow
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { RouteUtil } from 'utils/RouteUtil';
import { EventSelector } from 'modules/event/selector';
import { EventActions } from 'modules/event/actions';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Slider from 'react-slick';
import { RouteNames } from 'router';
import { CallToAction } from 'components/atoms/callToAction';
import { SectionIntro } from 'components/atoms/sectionIntro';
import { EventCard } from 'components/molecules/eventCard';
import { PaginationControl } from 'components/molecules/paginationControl';
import BellIcon from 'assets/images/bell1.png';
import { useViewport } from 'components/templates/viewportProvider';
import type { Event } from 'types/event';
import { OverlayPortal } from 'components/templates/overlayPortal';
import { OverlayModal } from 'components/templates/overlayModal';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { EventNotificationButton } from 'components/molecules/eventNotificationButton';
import './styles.scss';
import { CURRENT_COUNTRY_CODE } from 'config/env';
import { CountryTimeUtil } from 'utils/CountryTimeUtil';

type Props = {
  events: Array<Event>,
};

const UpcomingEventsCarousel = (props: Props) => {
  const { height } = useViewport();
  const [viewport, setViewport] = useState({
    width: 0,
    height: 0,
  });
  const [showEventsOverlay, setShowEventsOverlay] = useState(false);

  const updateWindowDimensions = () => {
    setViewport({
      ...viewport,
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  const [activeSlide, setActiveSlide] = useState(0);
  const [slider, setSlider] = useState();

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener('resize', updateWindowDimensions);
    return () => {
      window.removeEventListener('resize', updateWindowDimensions);
    };
  }, []);

  const {
    events,
    showNotificationButton,
    overlayData,
    filteredEvents,
    isFiltered,
    activeFilters,
  } = props;
  if (!events) {
    return null;
  }

  const countryCode = CURRENT_COUNTRY_CODE === '+1' ? 'USA' : 'INDIA';
  const validEvents = CountryTimeUtil.filterUpcomingValidEvents(events, countryCode);
  // Render content

  const renderIntro = () => {
    return (
      <SectionIntro
        title={'Browse Our Upcoming Shows'}
        description={
          'Feed your soul with spectacular live music that transcends linguistic and cultural barriers. Our venues are masterfully built for optimal sound quality and attract top international talent in jazz, blues, folk, classical and more. Browse upcoming shows at all our locations'
        }
        callToAction={
          <CallToAction
            text={'Browse all events'}
            onClick={() => setShowEventsOverlay(true)}
            mode={'light'}
            iconType={'events-button'}
            className={'browse-events-button'}
          />
        }
      />
    );
  };

  const settings = {
    className: 'slider variable-width events-carousel',
    infinite: true,
    centerMode: !(activeSlide === 0 || activeSlide === events.length),
    slidesToShow: 1.5,
    slidesToScroll: 1,
    variableWidth: true,
    speed: 1000,
    beforeChange: (current, next) => setActiveSlide(next),
    arrows: false,
    draggable: true,
    cssEase: 'cubic-bezier(0.77, 0, 0.175, 1)',
  };

  const renderContent = () => {
    return (
      <Slider ref={c => setSlider(c)} {...settings} style={{ height: '100%' }}>
        {renderIntro()}
        {validEvents?.map(upcomingEvent => {
          return (
            <EventCard
              data={upcomingEvent}
              onClick={() => onEventClick(upcomingEvent)}
              className={'upcoming-events-card'}
              key={upcomingEvent.id}
            />
          );
        })}
      </Slider>
    );
  };

  const onEventClick = event => {
    RouteUtil.setRouteChange(
      props,
      `${RouteNames.musicEvents}/${event.urlSlug}`
    );
  };

  // Render footer
  const next = () => {
    slider.slickNext();
  };

  const previous = () => {
    slider.slickPrev();
  };

  const renderFooter = () => {
    return (
      <div className={'footer-buttons'}>
        {showNotificationButton && (
          <EventNotificationButton
            theme={'dark-theme'}
            text={'GET NOTIFIED OF UPCOMING EVENTS'}
            link={'contact'}
          />
        )}
        <PaginationControl
          className={'section-footer-item'}
          theme={'dark-theme'}
          currentIndex={activeSlide !== 0 ? activeSlide : 1}
          total={events.length}
          onNextClick={next}
          onPreviousClick={previous}
        />
      </div>
    );
  };

  const showOverlay = showEventsOverlay ? (
    <OverlayPortal>
      <OverlayModal
        data={isFiltered ? filteredEvents : overlayData}
        theme={'light-theme'}
        type={'upcoming-events-desktop'}
        className={'upcoming-events-overlay-desktop'}
        onClosePress={() => setShowEventsOverlay(false)}
        breadcrumbList={[
          {
            breadcrumbName: 'Music & Events',
            breadcrumbLink: RouteNames.musicEvents,
          },
          {
            breadcrumbName: 'All Upcoming Events',
            breadcrumbLink: '#',
          },
        ]}
        activeFilters={activeFilters}
      />
    </OverlayPortal>
  ) : null;

  return (
    <div className={'section-container no-gutter'} style={{ height }}>
      {renderContent()}
      {renderFooter()}
      {showOverlay}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    filters: EventSelector.getUpcomingEventFilters(state),
    isFiltered: EventSelector.isUpcomingEventsFiltered(state),
    activeFilters: EventSelector.getActiveUpcomingEventFiltersNames(state),
    filteredEvents: EventSelector.getUpcomingFilteredEvents(state, 'upcoming'),
  };
};
const mapDispatchToProps = dispatch => {
  const { toggleUpcomingEventFilter } = EventActions;
  return bindActionCreators({ toggleUpcomingEventFilter }, dispatch);
};
const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(UpcomingEventsCarousel);
export default withRouter(connectedComponent);
